import React, { useMemo, useState } from 'react';
import type { Batiment, Personnel } from '../../../../shared/types/game';
import type { CtaOption } from '../CockpitTopbar';

/**
 * Tiroir « Implantations et sites » — port fidèle du prototype
 * (`network.js` drawerSites / carteSite) : recherche, regroupement par centre
 * d'appel (CTA), cartes de site avec agents / armables / engins, barres postes
 * et remise, extensions en cours. Branché sur les vraies données joueur.
 */

export interface BuildingRow {
  id: number | string;
  name: string;
  typeLabel: string;
  level: number;
  vehiclesCount: number;
  vehiclesReady: number;
  vehicleCapacity: number;
  personnelCount: number;
  personnelActive: number;
  personnelSlots: number;
  isPersonnelUpgradeRunning?: boolean;
  isVehicleUpgradeRunning?: boolean;
}

interface Props {
  buildings: Batiment[];
  rows: BuildingRow[];
  personnel: Personnel[];
  ctaList: CtaOption[];
  onOpenBuilding: (id: number) => void;
}

const isCta = (b: { type?: string }) => String(b.type ?? '').trim().toLowerCase() === 'cta';
const pct = (a: number, b: number) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0);

const CarteSite: React.FC<{ b: Batiment; row?: BuildingRow; onOpen: (id: number) => void }> = ({ b, row, onOpen }) => {
  const agents = row?.personnelCount ?? 0;
  const armables = row?.personnelActive ?? 0;
  const engins = row?.vehiclesCount ?? 0;
  const dispo = row?.vehiclesReady ?? 0;
  const capP = row?.personnelSlots ?? Math.max(0, Number(b.postes_disponibles || 0));
  const capV = row?.vehicleCapacity ?? Math.max(0, Number(b.capacite_vehicules || 0));
  const wip = Boolean(row?.isPersonnelUpgradeRunning || row?.isVehicleUpgradeRunning || b.augmentation_personnel_en_cours || b.augmentation_vehicules_en_cours);

  return (
    <div className={`scard ${b.actif ? '' : 'is-dead'}`}>
      <div className="scard__h">
        <span className="scard__ico">🏢</span>
        <div className="scard__id">
          <p className="scard__nom">{b.nom}</p>
          <p className="scard__sub">{row?.typeLabel || b.type} · niveau {row?.level ?? Number(b.niveau || 1)}</p>
        </div>
        {b.actif ? null : <span className="phase phase--wait">Hors service</span>}
      </div>

      <div className="scard__stats">
        <div className="stat"><b>{agents}</b><span>agents</span></div>
        <div className={`stat ${armables ? '' : 'is-red'}`}><b>{armables}</b><span>armables</span></div>
        {capV ? (
          <>
            <div className="stat"><b>{engins}</b><span>engins</span></div>
            <div className={`stat ${dispo ? '' : 'is-red'}`}><b>{dispo}</b><span>dispo</span></div>
          </>
        ) : null}
      </div>

      <div className="need">
        <span className="need__t">Postes</span>
        <span className="need__bar"><i style={{ width: `${pct(agents, capP)}%` }} /></span>
        <span className="need__c">{agents}/{capP}</span>
      </div>
      {capV ? (
        <div className="need">
          <span className="need__t">Remise</span>
          <span className="need__bar"><i style={{ width: `${pct(engins, capV)}%` }} /></span>
          <span className="need__c">{engins}/{capV}</span>
        </div>
      ) : null}
      {wip ? <p className="bsub">🏗 Extension en cours</p> : null}

      <button
        className="btn btn--sm btn--ghost btn--block"
        style={{ marginTop: 7 }}
        type="button"
        onClick={() => onOpen(Number(b.id))}
      >
        Gérer le site
      </button>
    </div>
  );
};

const SitesDrawer: React.FC<Props> = ({ buildings, rows, personnel, ctaList, onOpenBuilding }) => {
  const [q, setQ] = useState('');
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  const rowById = useMemo(() => {
    const m = new Map<number, BuildingRow>();
    rows.forEach((r) => m.set(Number(r.id), r));
    return m;
  }, [rows]);

  const ql = q.trim().toLowerCase();
  const match = (b: Batiment) => !ql || `${b.nom} ${rowById.get(Number(b.id))?.typeLabel || b.type} ${b.localisation || ''}`.toLowerCase().includes(ql);

  const ctaBuildings = useMemo(() => buildings.filter(isCta), [buildings]);
  const orphans = useMemo(
    () => buildings.filter((b) => !isCta(b) && b.cta_id == null && match(b)),
    [buildings, ql], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const toggleFold = (id: number) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const ctaMeta = (id: number) => ctaList.find((c) => c.id === id);
  const operatorsOf = (id: number) => personnel.filter((p) => Number(p.batiment_id) === id).length;

  return (
    <>
      <p className="dintro">
        Les modules de gestion s'ouvrent <b>par-dessus</b> la salle opérationnelle : la file continue
        de tourner derrière, <kbd>Échap</kbd> ramène au dispatch. Les sites sont regroupés par
        <b> centre d'appel</b> de rattachement.
      </p>

      <div className="toolbar toolbar--wide">
        <input
          type="search"
          placeholder="Rechercher un site, un centre, un département…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoComplete="off"
        />
        <button
          className="btn btn--sm btn--ghost"
          type="button"
          onClick={() =>
            setCollapsed((prev) => (prev.size ? new Set() : new Set(ctaBuildings.map((c) => Number(c.id)))))
          }
        >
          {collapsed.size ? 'Tout déplier' : 'Tout replier'}
        </button>
      </div>

      {ctaBuildings.map((c) => {
        const cid = Number(c.id);
        const sites = buildings.filter((b) => !isCta(b) && Number(b.cta_id) === cid);
        const visibles = sites.filter(match);
        if (ql && !match(c) && !visibles.length) return null;
        const meta = ctaMeta(cid);
        const replie = collapsed.has(cid);
        return (
          <div className="deptblock" key={cid}>
            <div className={`ctahead ${c.actif ? '' : 'is-dead'}`}>
              <button className="ctahead__fold" type="button" onClick={() => toggleFold(cid)} title={replie ? 'Déplier' : 'Replier'}>
                {replie ? '▸' : '▾'}
              </button>
              <div className="ctahead__id">
                <span className="sitem__ind mono">{meta?.ind || 'CTA'}</span>
                <div>
                  <p className="ctahead__nom">{c.nom}</p>
                  <p className="ctahead__sub">
                    {c.localisation ? `${c.localisation} · ` : ''}niveau {Number(c.niveau || 1)} · {operatorsOf(cid)} opérateur(s) · {sites.length} site(s) rattaché(s)
                  </p>
                </div>
              </div>
              <div className="ctahead__flags">
                <span className={`cbadge ${c.actif ? 'cbadge--on' : 'cbadge--off'}`}>{c.actif ? 'Actif' : 'Hors service'}</span>
                <button className="btn btn--sm btn--ghost" type="button" onClick={() => onOpenBuilding(cid)}>
                  Gérer le centre
                </button>
              </div>
            </div>
            {replie ? null : (
              <div className="grid-cards">
                {visibles.length ? (
                  visibles.map((b) => <CarteSite key={String(b.id)} b={b} row={rowById.get(Number(b.id))} onOpen={onOpenBuilding} />)
                ) : (
                  <p className="empty">Aucun site rattaché à ce centre.</p>
                )}
              </div>
            )}
          </div>
        );
      })}

      {orphans.length ? (
        <div className="deptblock">
          <div className="ctahead">
            <div className="ctahead__id">
              <span className="sitem__ind mono">—</span>
              <div>
                <p className="ctahead__nom">Sites non rattachés</p>
                <p className="ctahead__sub">Aucun centre d'appel ne les coordonne : ils ne reçoivent pas d'intervention.</p>
              </div>
            </div>
          </div>
          <div className="grid-cards">
            {orphans.map((b) => (
              <CarteSite key={String(b.id)} b={b} row={rowById.get(Number(b.id))} onOpen={onOpenBuilding} />
            ))}
          </div>
        </div>
      ) : null}

      {ctaBuildings.length === 0 && orphans.length === 0 ? (
        <div className="empty">Aucun site. Construisez-en un depuis la carte du dispatch.</div>
      ) : null}
    </>
  );
};

export default SitesDrawer;
